import { useMemo, useState } from 'react'
import { Bot, CircleAlert, CircleCheck } from 'lucide-react'
import { useAIVisibility } from '../../hooks/useCompetitiveData'
import HelpTooltip from './HelpTooltip'

const PLATFORM_ORDER = ['chatgpt', 'rufus', 'google_ai'] as const
const QUERY_PURCHASE_INTENT: Record<string, number> = {
  'dark spot corrector for dark skin': 95,
  'hyperpigmentation treatment dark skin': 93,
  'vitamin c serum for dark skin': 91,
  'best face moisturizer for black skin': 89,
  'skincare for melanin rich skin': 87,
  'skincare for dark skin': 84,
  'best skincare brand for dark skin': 80,
  'melanin skincare': 78,
  'fenty skin vs buttah': 72,
  'topicals vs buttah': 70,
}

function platformLabel(platform: string): string {
  if (platform === 'chatgpt') return 'ChatGPT'
  if (platform === 'rufus') return 'Rufus'
  if (platform === 'google_ai') return 'Google AI'
  return platform
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return 'Not available'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'Not available'
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function purchaseIntentScore(query: string): number {
  const normalized = query.trim().toLowerCase()
  return QUERY_PURCHASE_INTENT[normalized] ?? 50
}

export default function AIVisibilityPanel() {
  const [showFullReport, setShowFullReport] = useState(false)
  const { data, isLoading, error } = useAIVisibility()

  const snapshots = useMemo(() => data?.snapshots ?? [], [data?.snapshots])
  const summary = data?.summary
  const errorMessage = error instanceof Error ? error.message : null

  const byQuery = useMemo(() => {
    const grouped = new Map<string, Partial<Record<(typeof PLATFORM_ORDER)[number], (typeof snapshots)[number]>>>()
    for (const snapshot of snapshots) {
      const query = snapshot.query_text
      const platform = snapshot.platform as (typeof PLATFORM_ORDER)[number]
      if (!PLATFORM_ORDER.includes(platform)) continue
      const current = grouped.get(query) ?? {}
      current[platform] = snapshot
      grouped.set(query, current)
    }
    return grouped
  }, [snapshots])

  const rows = useMemo(
    () =>
      Array.from(byQuery.entries()).sort((left, right) => left[0].localeCompare(right[0], 'en-US')),
    [byQuery]
  )

  const missingQueries = useMemo(() => {
    const items: Array<{
      query: string
      coverageCount: number
      missingCount: number
      purchaseIntent: number
      missingPlatforms: string[]
      competitorPreview: string[]
    }> = []

    for (const [query, platforms] of rows) {
      let coverageCount = 0
      const missingPlatforms: string[] = []
      const competitorsSeen = new Set<string>()

      for (const platform of PLATFORM_ORDER) {
        const snapshot = platforms[platform]
        if (snapshot && snapshot.buttah_position !== null) {
          coverageCount += 1
          continue
        }

        missingPlatforms.push(platformLabel(platform))
        for (const brand of snapshot?.brands_shown ?? []) {
          const normalized = String(brand).trim()
          if (normalized) {
            competitorsSeen.add(normalized)
          }
        }
      }

      const missingCount = PLATFORM_ORDER.length - coverageCount
      if (missingCount <= 0) {
        continue
      }

      items.push({
        query,
        coverageCount,
        missingCount,
        purchaseIntent: purchaseIntentScore(query),
        missingPlatforms,
        competitorPreview: Array.from(competitorsSeen).slice(0, 3),
      })
    }

    return items.sort((left, right) => {
      if (right.purchaseIntent !== left.purchaseIntent) {
        return right.purchaseIntent - left.purchaseIntent
      }
      if (right.missingCount !== left.missingCount) {
        return right.missingCount - left.missingCount
      }
      return left.query.localeCompare(right.query, 'en-US')
    })
  }, [rows])

  return (
    <section className="rounded-2xl border border-[var(--patterniq-navy-200)] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h3 className="competitive-panel-title">AI Visibility</h3>
            <HelpTooltip
              title="AI Visibility"
              text="We check 30+ queries across ChatGPT, Amazon Rufus, and Google AI weekly."
            />
          </div>
          <p className="competitive-body-text mt-1">
            AI shopping recommendation monitoring across ChatGPT, Rufus, and Google AI.
          </p>
        </div>
        <div className="rounded-lg border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] px-3 py-2 text-right">
          <p className="competitive-support-label">Buttah Visibility</p>
          <p className="mt-1 text-xl font-bold text-[var(--patterniq-navy-900)]">
            {summary?.buttah_visibility_pct ?? 0}%
          </p>
          <p className="text-xs text-[var(--patterniq-navy-600)]">
            {summary?.buttah_visible_count ?? 0}/{summary?.total_queries ?? 0} query-platform snapshots
          </p>
        </div>
      </div>

      {errorMessage && (
        <div className="mt-4 rounded-lg border border-[var(--danger-100)] bg-[var(--danger-50)] p-3 text-sm text-[var(--danger-700)]">
          {errorMessage}
        </div>
      )}

      {isLoading ? (
        <div className="mt-4 rounded-lg border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] p-4 text-sm text-[var(--patterniq-navy-600)]">
          Loading AI visibility snapshots...
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-sm font-medium text-slate-900">
              Buttah is missing from {missingQueries.length} of {rows.length} tracked queries
            </p>
            <p className="mt-1 text-xs text-slate-500">Across ChatGPT, Amazon Rufus, and Google AI</p>
          </div>

          <div className="mt-4 rounded-xl border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] p-3">
            <p className="competitive-support-label">Top Opportunity Queries</p>
            <div className="mt-2 space-y-2">
              {missingQueries.length === 0 ? (
                <p className="text-sm text-[var(--patterniq-navy-600)]">Buttah appeared across all tracked queries.</p>
              ) : (
                missingQueries.slice(0, 3).map((item) => (
                  <div
                    key={`missing-${item.query}`}
                    className="rounded-lg border border-[var(--patterniq-navy-200)] bg-white px-2.5 py-2"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="truncate text-sm font-medium text-[var(--patterniq-navy-900)]">{item.query}</p>
                      <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                        Intent {item.purchaseIntent}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--danger-700)]">Missing on: {item.missingPlatforms.join(', ')}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowFullReport((prev) => !prev)}
            className="mt-3 text-sm font-semibold text-[var(--patterniq-navy-900)] hover:underline"
          >
            {showFullReport ? 'Hide full AI visibility report' : 'View full AI visibility report →'}
          </button>

          {showFullReport && (
            <div className="mt-4 overflow-x-auto rounded-xl border border-[var(--patterniq-navy-200)]">
              <table className="min-w-full border-collapse">
                <thead className="bg-[var(--patterniq-navy-50)]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wide text-[var(--patterniq-navy-700)]">
                      Query
                    </th>
                    {PLATFORM_ORDER.map((platform) => (
                      <th
                        key={platform}
                        className="px-3 py-2 text-left text-xs uppercase tracking-wide text-[var(--patterniq-navy-700)]"
                      >
                        {platformLabel(platform)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-6 text-center text-sm text-[var(--patterniq-navy-600)]"
                      >
                        No AI visibility snapshots yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map(([query, platforms]) => (
                      <tr key={query} className="border-t border-[var(--patterniq-navy-100)] align-top">
                        <td className="px-3 py-3 text-sm font-medium text-[var(--patterniq-navy-900)]">{query}</td>
                        {PLATFORM_ORDER.map((platform) => {
                          const cell = platforms[platform]
                          if (!cell) {
                            return (
                              <td key={`${query}-${platform}`} className="px-3 py-3 text-xs text-[var(--patterniq-navy-500)]">
                                No snapshot
                              </td>
                            )
                          }

                          const visible = cell.buttah_position !== null
                          return (
                            <td key={`${query}-${platform}`} className="px-3 py-3">
                              <div
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                  visible
                                    ? 'border-[var(--success-100)] bg-[var(--success-50)] text-[var(--success-700)]'
                                    : 'border-[var(--danger-100)] bg-[var(--danger-50)] text-[var(--danger-700)]'
                                }`}
                              >
                                {visible ? (
                                  <>
                                    <CircleCheck className="h-3.5 w-3.5" />
                                    Buttah present
                                  </>
                                ) : (
                                  <>
                                    <CircleAlert className="h-3.5 w-3.5" />
                                    Buttah missing
                                  </>
                                )}
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 flex items-center gap-1.5 text-xs text-[var(--patterniq-navy-500)]">
            <Bot className="h-3.5 w-3.5" />
            Last snapshot update: {formatTimestamp(data?.last_updated)}
          </p>
        </>
      )}
    </section>
  )
}
